import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft, Calendar, Clock, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { blogPosts } from './Blog';
import BookingModal from '../components/BookingModal';

const BlogPost = () => {
  const { slug } = useParams();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <main className="bg-black min-h-screen pt-32">
        <Helmet>
          <title>Article Not Found | BLR Nail Club Blog</title>
        </Helmet>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl text-white mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#B8860B] hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  const shareUrl = `https://www.blrnailclub.in/blog/${slug}`;

  return (
    <>
      <Helmet>
        <title>{`${post.title} | BLR Nail Club Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={`${post.category}, nail care, beauty tips, BLR Nail Club, Bangalore nail salon`} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image,
            "datePublished": post.date,
            "author": {
              "@type": "Organization",
              "name": "BLR Nail Club"
            },
            "publisher": {
              "@type": "Organization",
              "name": "BLR Nail Club",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.blrnailclub.in/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      <main className="bg-black min-h-screen" data-testid="blog-post-page">
        {/* Hero Image */}
        <div className="relative h-[50vh] md:h-[60vh]">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
          
          {/* Back Button */}
          <Link
            to="/blog"
            className="absolute top-28 left-6 md:left-12 flex items-center gap-2 text-white/80 hover:text-[#B8860B] transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">Back to Blog</span>
          </Link>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 md:px-12 -mt-32 relative z-10">
          {/* Category & Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="bg-[#B8860B] text-black px-4 py-1 text-xs tracking-wider uppercase">
              {post.category}
            </span>
            <span className="text-white/60 text-sm flex items-center gap-2">
              <Calendar size={14} />
              {new Date(post.date).toLocaleDateString('en-IN', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <span className="text-white/60 text-sm flex items-center gap-2">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
            {post.title}
          </h1>

          {/* Share Buttons */}
          <div className="flex items-center gap-4 mb-12 pb-8 border-b border-white/10">
            <span className="text-white/40 text-sm">Share:</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#B8860B] transition-colors"
            >
              <Facebook size={20} />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#B8860B] transition-colors"
            >
              <Twitter size={20} />
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#B8860B] transition-colors"
            >
              <Linkedin size={20} />
            </a>
          </div>

          {/* Article Content */}
          <article 
            className="prose prose-invert prose-lg max-w-none mb-16
              prose-headings:font-light prose-headings:text-white
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-[#B8860B]
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-white/70 prose-p:font-light prose-p:leading-relaxed
              prose-li:text-white/70 prose-li:font-light
              prose-strong:text-white prose-strong:font-medium
              prose-ul:my-4 prose-ol:my-4
              prose-a:text-[#B8860B] prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#B8860B]/20 to-[#B8860B]/10 rounded-lg p-8 md:p-12 mb-16">
            <h3 className="text-2xl font-light text-white mb-4">
              Ready to Experience Premium Nail Care?
            </h3>
            <p className="text-white/60 mb-6">
              Book your appointment at BLR Nail Club and let our experts pamper you with the best nail services in Bangalore.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="bg-[#B8860B] text-black px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#9A7B0A] transition-all duration-300"
            >
              Book Now
            </button>
          </div>

          {/* Related Posts */}
          <div className="mb-16">
            <h3 className="text-xl font-light text-white mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {blogPosts
                .filter(p => p.category === post.category && p.id !== post.id)
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group flex gap-4 bg-white/5 rounded-lg overflow-hidden hover:bg-white/10 transition-all"
                  >
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-32 h-32 object-cover flex-shrink-0"
                    />
                    <div className="py-4 pr-4">
                      <span className="text-[#B8860B] text-xs tracking-wider uppercase">
                        {relatedPost.category}
                      </span>
                      <h4 className="text-white font-light mt-1 group-hover:text-[#B8860B] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h4>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>

        {/* Booking Modal */}
        <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      </main>
    </>
  );
};

export default BlogPost;
